import { useState } from 'react';
import api from '@/lib/api';
import useCanvasStore from '@/store/canvasStore';
import useUiStore from '@/store/uiStore';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { RotateCcw, CheckCircle, XCircle, Zap } from 'lucide-react';

export default function QuizTab({ nodeId, nodeLabel, nodeDescription }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const { setNodeMastery } = useCanvasStore();
  const { openUpgradeModal } = useUiStore();
  const { user, refreshUser } = useAuth();

  const generateQuiz = async () => {
    setLoading(true);
    setQuestions([]);
    setAnswers({});
    setSubmitted(false);
    setScore(null);
    try {
      const res = await api.post('/ai/quiz', { nodeLabel, nodeDescription: nodeDescription || '' });
      setQuestions(res.data.questions || []);
      refreshUser();
    } catch (err) {
      if (err.response?.status === 403) openUpgradeModal();
    } finally { setLoading(false); }
  };

  const handleSubmit = () => {
    if (questions.length === 0) return;
    let correct = 0;
    questions.forEach(q => {
      if (answers[q.id] === q.correctIndex) correct++;
    });
    const pct = (correct / questions.length) * 100;
    setScore({ correct, total: questions.length, pct });
    setSubmitted(true);

    let mastery = 'learning';
    if (pct >= 80) mastery = 'mastered';
    else if (pct >= 60) mastery = 'competent';
    setNodeMastery(nodeId, mastery);
  };

  return (
    <div className="p-4" data-testid="quiz-tab">
      {/* Credits display for all users */}
      <div className="flex items-center gap-1.5 mb-3 px-2 py-1.5 bg-muted/40 rounded-md" data-testid="quiz-credits">
        <Zap className="w-3 h-3 text-cobalt" strokeWidth={1.5} />
        <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
          {user?.credits ?? 0} credit{user?.credits !== 1 ? 's' : ''} left
        </span>
      </div>

      {questions.length === 0 && !loading && (
        <div className="text-center py-8">
          <p className="font-body text-sm text-muted-foreground mb-3">Test your knowledge of</p>
          <p className="font-body text-sm font-medium mb-4">{nodeLabel}</p>
          <Button size="sm" onClick={generateQuiz} data-testid="generate-quiz-btn"
            className="bg-cobalt text-white hover:bg-cobalt/90 font-body text-xs btn-press">
            Generate Check
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center" data-testid="quiz-loading">
          <div className="spinner" />
          <span className="font-body text-sm text-muted-foreground">Creating quiz...</span>
        </div>
      )}

      {questions.length > 0 && (
        <div className="space-y-5">
          {questions.map((q, qi) => (
            <div key={q.id} className="border border-border rounded-md p-3" data-testid={`quiz-question-${qi}`}>
              <p className="font-body text-sm font-medium mb-2">
                <span className="font-mono text-[10px] text-muted-foreground mr-1">Q{qi + 1}</span>
                {q.question}
              </p>
              <div className="space-y-1.5">
                {q.options.map((opt, oi) => {
                  const isSelected = answers[q.id] === oi;
                  const isCorrect = submitted && oi === q.correctIndex;
                  const isWrong = submitted && isSelected && oi !== q.correctIndex;
                  return (
                    <button
                      key={oi}
                      onClick={() => { if (!submitted) setAnswers(a => ({ ...a, [q.id]: oi })); }}
                      className={`w-full text-left px-3 py-2 rounded-md text-xs font-body border transition-colors flex items-center gap-2 ${
                        isCorrect ? 'bg-green-50 border-green-300 text-green-800' :
                        isWrong ? 'bg-red-50 border-red-300 text-red-800' :
                        isSelected ? 'bg-cobalt/5 border-cobalt text-cobalt' :
                        'border-border hover:border-foreground/30'
                      }`}
                      disabled={submitted}
                      data-testid={`quiz-option-${qi}-${oi}`}
                    >
                      {isCorrect && <CheckCircle className="w-3 h-3 shrink-0" />}
                      {isWrong && <XCircle className="w-3 h-3 shrink-0" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {!submitted ? (
            <Button size="sm" onClick={handleSubmit}
              disabled={Object.keys(answers).length < questions.length}
              data-testid="quiz-submit-btn"
              className="w-full bg-cobalt text-white hover:bg-cobalt/90 font-body text-xs btn-press">
              Submit Answers
            </Button>
          ) : (
            <div className="border border-border rounded-md p-4 text-center" data-testid="quiz-results">
              <p className="font-heading text-2xl font-[500] mb-1">{score.correct}/{score.total}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3">
                {score.pct >= 80 ? 'Mastered!' : score.pct >= 60 ? 'Competent' : 'Keep Learning'}
              </p>
              <Button size="sm" variant="outline" onClick={generateQuiz} data-testid="quiz-retry-btn"
                className="font-body text-xs border-border">
                <RotateCcw className="w-3 h-3 mr-1" strokeWidth={1.5} /> Try Again
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
